import { useParams } from "react-router-dom";
import { CommonSignerView } from "@newPages/sign/CommonSignerView";
import { getInitialDocumentFile } from "@newComponents/common/Viewer/Viewer.function";
import { useBoolean } from "@newHooks/common/useBoolean";
import { DocumentContextProvider, MarkerZoneType } from "@newHooks/context/document/document.context";
import { DocumentMarker, DocumentMarkerProvider } from "@newHooks/context/document/documentMarker.context";
import { DocumentScrollContextProvider as NewDocumentScrollContextProvider } from "@newHooks/context/document/documentScroll.context";

import {
  DocumentSigner,
  DocumentSignerAttr,
  DocumentSignerAttrType,
  DocumentSignerStatus,
  DocumentSignResource,
  DocumentSignType,
} from "@maven-rest/docs/DocumentSign.dto";

interface DocumentSignerViewProps {
  documentSign: DocumentSignResource;
  isDownloadFilesCreating?: boolean;
  onDocumentRefresh: () => void;
}

export function DocumentSignerView({ documentSign, onDocumentRefresh, isDownloadFilesCreating = false }: DocumentSignerViewProps) {
  const { token } = useParams<{ token: string }>();
  const isAgentAvailable = useBoolean(false);

  const documentSigner = documentSign.signers
    .concat([])
    .reverse()
    .find((x) => x.contentUrl)!;

  const resolveSignatureFields = (): Array<DocumentSignerAttr> => {
    if (documentSign.type === DocumentSignType.EConsent) {
      return documentSigner.attrs.filter(
        (e) => (isAgentAvailable.value ? DocumentSignerAttrType.AgentSignature : DocumentSignerAttrType.Signature) === e.attr
      );
    }

    return documentSigner.attrs.filter((e) => e.attr === DocumentSignerAttrType.Signature);
  };

  return (
    <DocumentContextProvider
      mode={"SIGNING"}
      documentFile={getInitialDocumentFile(documentSigner, documentSign)}
      markerZoneType={resolveMarkerZoneType(documentSign.type)}
    >
      <DocumentMarkerProvider markers={resolveMarkers(documentSigner)}>
        <NewDocumentScrollContextProvider>
          <CommonSignerView
            token={token}
            onRefreshDocument={onDocumentRefresh}
            isDownloadFilesCreating={isDownloadFilesCreating}
            documentSign={documentSign}
            documentSigner={documentSigner}
            isAgentAvailable={isAgentAvailable}
            signatureFields={resolveSignatureFields()}
          />
        </NewDocumentScrollContextProvider>
      </DocumentMarkerProvider>
    </DocumentContextProvider>
  );
}

function resolveMarkerZoneType(documentSignType: DocumentSignType): MarkerZoneType {
  return documentSignType === DocumentSignType.EConsent ? MarkerZoneType.EConsent : MarkerZoneType.ESign;
}

function resolveMarkers(documentSigner: DocumentSigner): Array<DocumentMarker> {
  const isSignDeliverOrOpen = (status: DocumentSignerStatus): boolean => {
    return [DocumentSignerStatus.Delivered, DocumentSignerStatus.DeliveringSkipped, DocumentSignerStatus.Opened].includes(status);
  };

  return documentSigner.isMine && isSignDeliverOrOpen(documentSigner.status)
    ? documentSigner.attrs.map((e) => ({
        id: e.id,
        signerUuid: String(documentSigner.id),
        target: e.target,
        signerIndex: documentSigner.signerIndex,
        groupUuid: e.groupUuid,
        attrUuid: e.attrUuid,
        attr: e.attr,
        page: e.pageIndex,
        value: e.value,
        x: e.coordX,
        y: e.coordY,
      }))
    : [];
}
